'use client'

import { useState } from 'react'
import { Send } from 'lucide-react'
import { isValidEmail, validateEmailMx, type EmailMxResult } from '@sifly/shared-utils'
import MxHint from './MxHint'

export type EmailSignupProps = {
  headline?: string
  subtext?: string
  incentive?: string
  variant?: 'minimal' | 'full'
  onSubmit?: (email: string) => void
}

export function EmailSignup({ headline, subtext, incentive, variant = 'full', onSubmit }: EmailSignupProps) {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [mx, setMx] = useState<EmailMxResult>(null)
  const [error, setError] = useState<string | null>(null)

  function handleBlur() {
    const trimmed = email.trim()
    if (!trimmed) { setMx(null); setError(null); return }
    if (!isValidEmail(trimmed)) {
      setError('Please enter a valid email address.')
      setMx(null)
      return
    }
    setError(null)
    validateEmailMx(trimmed).then(setMx)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed) return
    if (!isValidEmail(trimmed)) {
      setError('Please enter a valid email address.')
      return
    }
    onSubmit?.(trimmed)
    setSubmitted(true)
  }

  if (submitted) {
    return (
      <div className="py-8 px-4 text-center" data-component="EmailSignup">
        <p className="text-[var(--color-text-primary)] font-medium" style={{ fontFamily: 'var(--font-heading)' }}>
          Thanks for subscribing!
        </p>
      </div>
    )
  }

  const onInputChange = (v: string) => { setEmail(v); if (mx) setMx(null); if (error) setError(null) }

  if (variant === 'minimal') {
    return (
      <form onSubmit={handleSubmit} className="max-w-md mx-auto px-4" data-component="EmailSignup">
        <div className="flex gap-2">
          <input
            type="email" required value={email} onChange={(e) => onInputChange(e.target.value)}
            onBlur={handleBlur}
            placeholder="Enter your email"
            className="flex-1 px-4 py-2.5 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-bg-primary)] text-sm text-[var(--color-text-primary)]"
          />
          <button type="submit" className="bg-[var(--color-cta)] text-[var(--color-cta-text)] px-4 py-2.5 rounded-[var(--radius-sm)] hover:opacity-90 transition-opacity">
            <Send className="w-4 h-4" />
          </button>
        </div>
        {error && <p className="text-xs text-[var(--color-error-strong)] mt-1">{error}</p>}
        <MxHint mx={mx} onPick={s => { setEmail(s); setMx(null) }} />
      </form>
    )
  }

  return (
    <section className="py-12 px-4 bg-[var(--color-bg-secondary)] text-center" data-component="EmailSignup">
      <div className="max-w-lg mx-auto">
        {headline && <h3 className="text-xl font-bold text-[var(--color-text-primary)]" style={{ fontFamily: 'var(--font-heading)' }}>{headline}</h3>}
        {subtext && <p className="mt-2 text-sm text-[var(--color-text-secondary)]" style={{ fontFamily: 'var(--font-body)' }}>{subtext}</p>}
        {incentive && <p className="mt-1 text-sm font-medium text-[var(--color-cta-readable)]">{incentive}</p>}
        <form onSubmit={handleSubmit} className="mt-4">
          <div className="flex gap-2">
            <input
              type="email" required value={email} onChange={(e) => onInputChange(e.target.value)}
              onBlur={handleBlur}
              placeholder="Enter your email"
              className="flex-1 px-4 py-2.5 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-bg-primary)] text-sm text-[var(--color-text-primary)]"
            />
            <button type="submit" className="bg-[var(--color-cta)] text-[var(--color-cta-text)] px-5 py-2.5 rounded-[var(--radius-sm)] font-medium hover:opacity-90 transition-opacity">
              Subscribe
            </button>
          </div>
          {error && <p className="text-xs text-[var(--color-error-strong)] mt-1 text-left">{error}</p>}
          <div className="text-left"><MxHint mx={mx} onPick={s => { setEmail(s); setMx(null) }} /></div>
        </form>
      </div>
    </section>
  )
}
